import {Box, Skeleton} from '@mantine/core';
import type React from 'react';
import {useState} from 'react';
import {COMPANIES_CRUMB, HOME_CRUMB} from '../../../config/constants/crumb';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {COMPANIES, HOME} from '../../../config/constants/routes';
import {
  CONSULTANT_INDUSTRIES,
  FOLLOW_COMPANY_RANKING_TERMS,
} from '../../../core/entities/company/constants';
import type {FollowRankingListTermType} from '../../../core/entities/company/schema';
import {
  type ResultGetWorkAttractiveConditions,
  useGetFollowAdCompanies,
  useGetFollowCompanyRankingList,
  useGetGroupedSupportCompanies,
} from '../../../core/repositories/companyRepository';
import type {IndustryCategoriesReturnValueType} from '../../../core/repositories/reportsRepository';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {sortWorkAttractiveConditions} from '../usecase/sortWorkAttractiveConditions';
import {useHandleCompanyForSearch} from '../usecase/useHandleCompanyForSearch';
import {useSyncAttractiveConditionWithQuery} from '../usecase/useSyncAttractiveConditionWithQuery';
import {CompaniesFrameContainer} from './CompaniesFrame/container';
import {CompanyListHeader} from './Container/CompanyListHeader';
import {CompanyCardPickup} from './Container/Presenter/CompanyCardPickup';
import {CompanyFilter} from './Container/Presenter/CompanyFilter';
import {CompanyTagSelector} from './Container/Presenter/CompanyTagSelector';
import {FollowCompanyRankingFrame} from './Container/Presenter/FollowCompanyRankingFrame';
import {CustomTagCompaniesFrameContainers} from './CustomTagCompaniesFrame/containers';
import {EngineerCompaniesFrameContainer} from './EngineerCompaniesFrame/container';

type Props = {
  title: string;
  industryCategories?: IndustryCategoriesReturnValueType;
  attractiveConditionsData?: ResultGetWorkAttractiveConditions;
  attractiveConditionQuery?: string[];
};

export const CompanyListPage: React.FC<Props> = ({
  title,
  industryCategories,
  attractiveConditionsData,
  attractiveConditionQuery = [],
}) => {
  // SSR初期値をクエリから同期
  const [filterAttractiveConditions, setFilterAttractiveConditions] =
    useSyncAttractiveConditionWithQuery(attractiveConditionQuery);
  const {
    searchWord,
    handleSearchInputChange,
    handleFilterEmployeeCountChange,
    filterMinEmployeeCount,
    filterMaxEmployeeCount,
    attractiveConditionsList,
  } = useHandleCompanyForSearch(
    industryCategories,
    attractiveConditionsData,
    filterAttractiveConditions,
  );
  // DFPバナーのセット
  const googleAdBannerNames = [GoogleAdBannerName.RICH_DESKTOP_HEADER];
  useGoogleAdBanner(googleAdBannerNames);
  const crumbs = [
    {label: HOME_CRUMB, href: HOME},
    {label: COMPANIES_CRUMB, href: COMPANIES},
  ];

  const {user} = useAuth();
  const isEngineer = user?.isEngineer;

  const [counts, setCounts] = useState<{[key: string]: number}>({});

  const handleSetTotalCount = (key: string, count: number) => {
    setCounts((prevCounts) => ({
      ...prevCounts,
      [key]: count,
    }));
  };
  const totalCount = Object.values(counts).reduce(
    (sum, count) => sum + count,
    0,
  );

  const {data: adCompanies, isLoading: isLoadingAdCompanies} =
    useGetFollowAdCompanies({
      queryParams: {
        limit: 20,
        contractOnly: true,
        minEmployeeCount: filterMinEmployeeCount,
        maxEmployeeCount: filterMaxEmployeeCount,
        workAttractiveConditionIDs: filterAttractiveConditions.map((item) =>
          Number(item),
        ),
      },
    });
  const convertAdCompanies = adCompanies?.ok
    ? (adCompanies.value.companies.map((company) => ({
        ...company,
        workAttractiveConditions: sortWorkAttractiveConditions(
          company.workAttractiveConditions,
          filterAttractiveConditions,
        ),
      })) ?? [])
    : [];

  const [followRankingListTerm, setFollowRankingListTerm] =
    useState<FollowRankingListTermType>(FOLLOW_COMPANY_RANKING_TERMS.daily);
  const [isLastYearRanking, setIsLastYearRanking] = useState(false);

  const {data: followRankingList, isLoading: isLoadingFollowRankingList} =
    useGetFollowCompanyRankingList({
      limit: 10,
      term:
        followRankingListTerm === FOLLOW_COMPANY_RANKING_TERMS.all
          ? undefined
          : followRankingListTerm,
      isLastYear: isLastYearRanking,
    });

  const {data} = useGetGroupedSupportCompanies({queryParams: {}});
  const companiesForSearch = data?.ok
    ? data.value.companyGroups.flatMap((group) => group.companies)
    : [];

  const isDisplayPickup =
    (isLoadingAdCompanies ||
      (adCompanies?.ok && adCompanies.value.companies.length > 0)) ??
    false;

  const isDisPlayRankingList =
    (isLoadingFollowRankingList || followRankingList?.ok) ?? false;

  return (
    <>
      <Box className="hidden pc:block">
        <GoogleAdBanner
          googleAdBannerName={GoogleAdBannerName.RICH_DESKTOP_HEADER}
        />
      </Box>
      <Box className="w-full mx-auto flex pc:w-[1200px] justify-between">
        <Box className="w-full pc:w-[1200px]">
          <CompanyListHeader pageTitle={title} crumbs={crumbs} />
          <Box className="bg-white mx-16 rounded-16 shadow-card pc:bg-transparent pc:mx-0 pc:rounded-none pc:shadow-transparent">
            <CompanyTagSelector
              industryCategories={industryCategories}
              isEngineer={isEngineer ?? false}
              isDisplayPickup={isDisplayPickup}
              isDisplayRanking={isDisPlayRankingList}
            />
            <CompanyFilter
              companies={companiesForSearch}
              searchWord={searchWord}
              handleSearchInputChange={handleSearchInputChange}
              handleFilterEmployeeCountChange={handleFilterEmployeeCountChange}
              attractiveConditionsList={attractiveConditionsList}
              handleFilterAttractiveConditionChange={
                setFilterAttractiveConditions
              }
              filterAttractiveConditions={filterAttractiveConditions}
            />
          </Box>
        </Box>
      </Box>
      {isDisplayPickup && (
        <div id="pickup" className="w-full bg-white pt-16 px-16 pc:px-0">
          <div className="mx-auto pc:w-[1200px]">
            <h2 className="font-bold text-black text-xl">
              外資就活ピックアップ
            </h2>
            <div className="flex mt-8 gap-16 pc:gap-24 overflow-x-scroll pb-8 mb-8">
              {adCompanies?.ok &&
                convertAdCompanies.map((company, index) => (
                  <CompanyCardPickup company={company} key={index} />
                ))}
              {isLoadingAdCompanies &&
                Array.from({length: 5}).map((_, index) => (
                  <div key={index}>
                    <Skeleton height={150} width={369} />
                    <Skeleton height={30} width={369} />
                    <Skeleton height={30} width={369} />
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
      <CustomTagCompaniesFrameContainers
        filterQuery={{
          minEmployeeCount: filterMinEmployeeCount,
          maxEmployeeCount: filterMaxEmployeeCount,
          workAttractiveConditionIDs: filterAttractiveConditions.map((item) =>
            Number(item),
          ),
        }}
        withOdd
        handleSetTotalCount={handleSetTotalCount}
        page="companies"
      />
      {isDisPlayRankingList && (
        <FollowCompanyRankingFrame
          ranking={followRankingList}
          isLoadingRanking={isLoadingFollowRankingList}
          rankingTerm={followRankingListTerm}
          setRankingTerm={setFollowRankingListTerm}
          isLastYearRanking={isLastYearRanking}
          setIsLastYearRanking={setIsLastYearRanking}
        />
      )}
      {isEngineer && (
        <EngineerCompaniesFrameContainer
          componentID="engineer"
          filterQuery={{
            minEmployeeCount: filterMinEmployeeCount,
            maxEmployeeCount: filterMaxEmployeeCount,
            workAttractiveConditionIDs: filterAttractiveConditions.map((item) =>
              Number(item),
            ),
          }}
          handleSetTotalCount={handleSetTotalCount}
        />
      )}
      {CONSULTANT_INDUSTRIES.map((industry) => (
        <CompaniesFrameContainer
          key={`industry-frame-${industry.ids[0]}`}
          componentID={`industry-${industry.ids[0]}`}
          item={{
            id: industry.ids[0] ?? 0,
            name: industry.name,
          }}
          filterQuery={{
            industryIDs: industry.ids,
            industryCategoryIDs: [1],
            minEmployeeCount: filterMinEmployeeCount,
            maxEmployeeCount: filterMaxEmployeeCount,
            workAttractiveConditionIDs: filterAttractiveConditions.map((item) =>
              Number(item),
            ),
          }}
          handleSetTotalCount={handleSetTotalCount}
          isConsultant={true}
        />
      ))}
      {industryCategories?.ok &&
        industryCategories.value.industryCategories
          .filter((category) => category.id !== 1) // コンサル・シンクタンクは除外
          .map((item) => (
            <CompaniesFrameContainer
              key={`industry-category-frame-${item.id}`}
              componentID={`industry-category-${item.id}`}
              item={item}
              filterQuery={{
                industryCategoryIDs: [item.id],
                minEmployeeCount: filterMinEmployeeCount,
                maxEmployeeCount: filterMaxEmployeeCount,
                workAttractiveConditionIDs: filterAttractiveConditions.map(
                  (item) => Number(item),
                ),
              }}
              handleSetTotalCount={handleSetTotalCount}
              isConsultant={false}
            />
          ))}
      {!isEngineer && (
        <EngineerCompaniesFrameContainer
          componentID="engineer"
          filterQuery={{
            minEmployeeCount: filterMinEmployeeCount,
            maxEmployeeCount: filterMaxEmployeeCount,
            workAttractiveConditionIDs: filterAttractiveConditions.map((item) =>
              Number(item),
            ),
          }}
          handleSetTotalCount={handleSetTotalCount}
        />
      )}
      {totalCount === 0 && (
        <div className="w-full bg-white py-16 px-16 pc:px-0">
          <div className="w-full flex justify-center items-center">
            <p className="text-black-2">企業が見つかりませんでした。</p>
          </div>
        </div>
      )}
    </>
  );
};
